import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useJobs } from '../contexts/JobsContext';
import { JobCard } from './JobCard';

export const JobsPanel: React.FC = () => {
  const { showJobsPanel, setShowJobsPanel, jobs, runningJobs, stats, isLoading, error, refreshJobs } = useJobs();
  const [activeTab, setActiveTab] = useState<'running' | 'history'>('running');

  // When panel opens and there are running jobs, switch to running tab
  useEffect(() => {
    if (showJobsPanel && runningJobs.length > 0) {
      setActiveTab('running');
    }
  }, [showJobsPanel, runningJobs.length]);

  const handleClose = () => {
    setShowJobsPanel(false);
  };

  const historyJobs = jobs.filter(job => job.status !== 'running');

  const displayedJobs = activeTab === 'running' ? runningJobs : historyJobs;

  return (
    <Drawer
      anchor="right"
      open={showJobsPanel}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Export Jobs</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={refreshJobs} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Stats */}
        {stats && (
          <Box sx={{ p: 2, bgcolor: 'background.default', display: 'flex', gap: 2, justifyContent: 'space-around' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">{stats.running}</Typography>
              <Typography variant="caption" color="text.secondary">Running</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">{stats.completed}</Typography>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">{stats.failed}</Typography>
              <Typography variant="caption" color="text.secondary">Failed</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">{stats.cancelled}</Typography>
              <Typography variant="caption" color="text.secondary">Cancelled</Typography>
            </Box>
          </Box>
        )}

        <Divider />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab
              label={
                <Badge badgeContent={runningJobs.length} color="primary">
                  Running
                </Badge>
              }
              value="running"
            />
            <Tab
              label={
                <Badge badgeContent={historyJobs.length} color="default">
                  History
                </Badge>
              }
              value="history"
            />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading && displayedJobs.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : displayedJobs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body2" color="text.secondary">
                {activeTab === 'running' 
                  ? 'No active exports' 
                  : 'No export history'}
              </Typography>
              {activeTab === 'running' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Start an export from the Content Manager tab
                </Typography>
              )}
            </Box>
          ) : (
            <>
              {displayedJobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </>
          )}
        </Box>

        {/* Footer */}
        {historyJobs.length > 0 && activeTab === 'history' && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              History is automatically cleared after 24 hours
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
